// Routes rendered on the redesigned dark sidebar shell (PublicSidebar) instead
// of the classic top navbar. These pages carry their own hero and (where
// applicable) footer band. Layout renders PublicSidebar/PublicMobileHeader for
// them directly, outside each page's own Suspense boundary, so the rail nav
// stays mounted and static while the center column loads.
// Extended one screen group at a time as each page is rebuilt on the shell —
// do not add a route here until its page actually renders on this shell.
export const SIDEBAR_SHELL_EXACT_ROUTES = new Set([
  "/",
  "/songs",
  "/playlists",
  "/artists",
  "/charts",
  "/events",
  "/plans",
  "/sponsors",
  "/support",
  "/competitions",
  "/verification",
  "/verification/success",
  "/terms",
  "/privacy",
  "/copyright",
  "/aml-policy",
]);

export const SIDEBAR_SHELL_PREFIXES: string[] = [
  "/playlists/",
  "/events/",
  "/profiles/",
  "/tracks/",
  "/albums/",
  "/competitions/",
];

// The sidebar-shell's primary content pages — these get the shared
// FooterBand (Layout.tsx renders it as the last element of each page's
// scrollable content). Legal pages (/terms, /privacy, ...) and the
// verification flow are deliberately left out: a footer linking back to
// Terms/Privacy at the bottom of the Terms page itself is circular, and the
// verification flow is a focused task rather than a page to browse away
// from.
export const FOOTER_BAND_ROUTES = new Set([
  "/",
  "/songs",
  "/playlists",
  "/artists",
  "/charts",
  "/events",
  "/plans",
  "/sponsors",
  "/support",
  "/competitions",
]);

export const usesSidebarShell = (pathname: string): boolean =>
  SIDEBAR_SHELL_EXACT_ROUTES.has(pathname) ||
  SIDEBAR_SHELL_PREFIXES.some((prefix) => pathname.startsWith(prefix));

// Role dashboards (artist/fan/sponsor) render their own persistent sidebar
// via shadcn's SidebarProvider, independent of PublicSidebar above — they
// need the same top-navbar/footer suppression but must NOT get
// PublicSidebar/PublicMobileHeader layered on top of their own sidebar.
//
// Edit Profile is reached from every role (via each dashboard's own
// sidebar footer, or the account menu) and mirrors whichever role's own
// sidebar sent the user there, so it self-manages its shell the same way
// rather than being layered under PublicSidebar's public/marketing nav.
export const DASHBOARD_SHELL_EXACT_ROUTES = new Set([
  "/dashboard/artist",
  "/dashboard/fan",
  "/dashboard/sponsor",
  "/dashboard/admin",
  "/profile",
  "/my-support-tickets",
]);

// Nested drill-down pages reached from a role dashboard (e.g. an artist
// opening one of their own albums) aren't part of the tabbed dashboard
// shell itself, but still need the classic top navbar/footer suppressed —
// they carry their own light-themed "back to dashboard" chrome instead of
// being layered under the old public marketing navbar.
export const DASHBOARD_SHELL_PREFIXES: string[] = ["/dashboard/artist/albums/"];

export const usesDashboardShell = (pathname: string): boolean =>
  DASHBOARD_SHELL_EXACT_ROUTES.has(pathname) ||
  DASHBOARD_SHELL_PREFIXES.some((prefix) => pathname.startsWith(prefix));

// Maps a sidebar-shell pathname to the rail item it should highlight, shared
// by the desktop rail (PublicSidebar) and the mobile/tablet bottom nav so
// both stay in sync. Ambiguous cases (a profile page whose role isn't known
// from the URL alone) default to a reasonable guess here and are corrected
// once the page's own data loads, via useSidebarActiveOverride.
export type PublicSidebarSectionId =
  | "home"
  | "music"
  | "playlists"
  | "artists"
  | "charts"
  | "events"
  | "plans"
  | "sponsors"
  | "support";

export const getSidebarSection = (
  pathname: string,
): PublicSidebarSectionId | null => {
  if (pathname === "/") return "home";
  if (
    pathname === "/songs" ||
    pathname.startsWith("/tracks/") ||
    pathname.startsWith("/albums/")
  )
    return "music";
  if (pathname === "/playlists" || pathname.startsWith("/playlists/"))
    return "playlists";
  if (pathname === "/artists") return "artists";
  if (pathname === "/charts") return "charts";
  if (pathname === "/events" || pathname.startsWith("/events/"))
    return "events";
  if (pathname === "/plans") return "plans";
  if (pathname === "/sponsors") return "sponsors";
  if (pathname === "/support") return "support";
  // Verification is the paid upgrade flow reached from the Plans page CTA,
  // so it highlights Plans rather than introducing its own rail section.
  if (pathname === "/verification" || pathname === "/verification/success")
    return "plans";
  if (pathname.startsWith("/profiles/")) return "artists";
  return null;
};
